import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownOutlined,
  EyeOutlined,
  DownloadOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Key } from 'react';
import { useTranslation } from 'react-i18next';
import LinkButton from 'components/link-button';
import {
  calculateShopSaleIncentive,
  getShopSaleIncentiveItems,
  getShopSaleIncentiveList,
  refreshShopSaleIncentive,
} from 'apis/shop-sale-incentive';
import type {
  ShopSaleIncentiveMonth,
  ShopSaleIncentiveMonthItem,
  ShopSaleIncentiveMonthStatus,
} from 'types/shop-sale-incentive';
import formatTime from 'utils/formatTime';
import exportToExcel, { exportToCsv } from 'utils/exportToExcel';
import LOCALS from 'commons/locals';

const { Title } = Typography;

const PAGE_SIZE = 10;
const ITEM_PAGE_SIZE = 100;
const EXPORT_ITEM_PAGE_SIZE = 500;

type ExportFormat = 'xlsx' | 'csv';

const moneyText = (value?: string | null) => value || '-';

/** 接口金额是字符串。空值留空；能解析的写成数字，避免 Excel 把整列当成文本。 */
const toExportAmount = (value?: string | null): number | string | null => {
  if (value == null) return null;
  const normalized = value.replace(/,/g, '').trim();
  if (!normalized) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : value;
};

const parseExportAmount = (value?: string | null): number => {
  const amount = toExportAmount(value);
  return typeof amount === 'number' ? amount : 0;
};

const roundExportAmount = (value: number) => Math.round(value * 100) / 100;

const nowrapColumnTitle = (title: string) => (
  <span className="whitespace-nowrap">{title}</span>
);

const nowrapCellText = (text: string) => (
  <span className="whitespace-nowrap">{text}</span>
);

const renderTime = (value?: string | null) =>
  nowrapCellText(value ? formatTime(value) : '-');

const withNowrapColumnTitles = <T extends object>(
  columns: ColumnsType<T>,
): ColumnsType<T> =>
  columns.map((column) => ({
    ...column,
    title:
      typeof column.title === 'string'
        ? nowrapColumnTitle(column.title)
        : column.title,
  })) as ColumnsType<T>;

type ListFilterValues = {
  yearMonth?: Dayjs | null;
  status?: ShopSaleIncentiveMonthStatus | null;
};

type ItemFilterValues = {
  orderId?: number | null;
  productId?: number | null;
};

type ItemPageState = {
  list: ShopSaleIncentiveMonthItem[];
  total: number;
  pageNum: number;
  pageSize: number;
  loading: boolean;
  filters: ItemFilterValues;
};

const emptyItemPage = (): ItemPageState => ({
  list: [],
  total: 0,
  pageNum: 1,
  pageSize: ITEM_PAGE_SIZE,
  loading: false,
  filters: {},
});

export default function ShopSaleIncentivePage() {
  const { t } = useTranslation();
  const [listForm] = Form.useForm<ListFilterValues>();
  const [itemForm] = Form.useForm<ItemFilterValues>();
  const [calculateForm] = Form.useForm<{ yearMonth: Dayjs }>();

  const productSourceMap: Record<number, { label: string; color: string }> = {
    0: { label: t(LOCALS.recycle), color: 'blue' },
    1: { label: t(LOCALS.consignment), color: 'green' },
    2: { label: t(LOCALS.commission_snapshot_self_owned), color: 'orange' },
  };

  const statusMap: Record<
    ShopSaleIncentiveMonthStatus,
    { label: string; color: string }
  > = {
    running: {
      label: t(LOCALS.shop_sale_incentive_status_running),
      color: 'processing',
    },
    completed: {
      label: t(LOCALS.shop_sale_incentive_status_completed),
      color: 'success',
    },
    failed: {
      label: t(LOCALS.shop_sale_incentive_status_failed),
      color: 'error',
    },
  };

  const rowTypeMap: Record<string, { label: string; color: string }> = {
    sale: {
      label: t(LOCALS.shop_sale_incentive_row_type_sale),
      color: 'blue',
    },
    refund: {
      label: t(LOCALS.shop_sale_incentive_row_type_refund),
      color: 'orange',
    },
  };

  const [list, setList] = useState<ShopSaleIncentiveMonth[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const pageSizeRef = useRef(PAGE_SIZE);
  const [listLoading, setListLoading] = useState(false);
  const latestListRequestRef = useRef(0);

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);
  const [itemsByMonthId, setItemsByMonthId] = useState<
    Record<number, ItemPageState>
  >({});

  const [calculateOpen, setCalculateOpen] = useState(false);
  const [calculateLoading, setCalculateLoading] = useState(false);
  const [refreshingYearMonth, setRefreshingYearMonth] = useState<string | null>(
    null,
  );
  const [downloadingMonthId, setDownloadingMonthId] = useState<number | null>(
    null,
  );

  const getListFilters = useCallback((): ListFilterValues => {
    return listForm.getFieldsValue();
  }, [listForm]);

  const toListQuery = (values: ListFilterValues) => ({
    ...(values.yearMonth
      ? { yearMonth: values.yearMonth.format('YYYY-MM') }
      : {}),
    ...(values.status ? { status: values.status } : {}),
  });

  const fetchList = useCallback(
    async (page: number, values: ListFilterValues = getListFilters()) => {
      const requestId = latestListRequestRef.current + 1;
      latestListRequestRef.current = requestId;
      setListLoading(true);
      try {
        const res = await getShopSaleIncentiveList({
          pageNum: page,
          pageSize: pageSizeRef.current,
          ...toListQuery(values),
        });
        if (latestListRequestRef.current !== requestId) return;
        setList(res.list);
        setTotal(res.total);
      } finally {
        if (latestListRequestRef.current === requestId) {
          setListLoading(false);
        }
      }
    },
    [getListFilters],
  );

  const fetchItems = useCallback(
    async (
      monthId: number,
      page: number,
      pageSizeValue: number,
      filters: ItemFilterValues = {},
    ) => {
      setItemsByMonthId((prev) => ({
        ...prev,
        [monthId]: {
          ...(prev[monthId] ?? emptyItemPage()),
          loading: true,
          pageNum: page,
          pageSize: pageSizeValue,
          filters,
        },
      }));
      try {
        const res = await getShopSaleIncentiveItems({
          monthId,
          pageNum: page,
          pageSize: pageSizeValue,
          ...(filters.orderId ? { orderId: filters.orderId } : {}),
          ...(filters.productId ? { productId: filters.productId } : {}),
        });
        setItemsByMonthId((prev) => ({
          ...prev,
          [monthId]: {
            list: res.list,
            total: res.total,
            pageNum: page,
            pageSize: pageSizeValue,
            loading: false,
            filters,
          },
        }));
      } catch {
        setItemsByMonthId((prev) => ({
          ...prev,
          [monthId]: {
            ...(prev[monthId] ?? emptyItemPage()),
            loading: false,
          },
        }));
      }
    },
    [],
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  const handleListSearch = (values: ListFilterValues) => {
    setPageNum(1);
    setExpandedRowKeys([]);
    fetchList(1, values);
  };

  const handleListReset = () => {
    listForm.resetFields();
    setPageNum(1);
    setExpandedRowKeys([]);
    fetchList(1, {});
  };

  const handleCalculate = async () => {
    const values = await calculateForm.validateFields();
    const yearMonth = values.yearMonth.format('YYYY-MM');
    setCalculateLoading(true);
    try {
      const res = await calculateShopSaleIncentive(yearMonth);
      message.success(
        t(LOCALS.shop_sale_incentive_calculate_success, {
          itemCount: res.itemCount ?? 0,
        }),
      );
      setCalculateOpen(false);
      calculateForm.resetFields();
      setPageNum(1);
      setExpandedRowKeys([]);
      fetchList(1);
    } finally {
      setCalculateLoading(false);
    }
  };

  const handleRefresh = async (record: ShopSaleIncentiveMonth) => {
    setRefreshingYearMonth(record.yearMonth);
    try {
      const res = await refreshShopSaleIncentive(record.yearMonth);
      message.success(
        t(LOCALS.shop_sale_incentive_refresh_success, {
          itemCount: res.itemCount ?? 0,
        }),
      );
      fetchList(pageNum);
      if (expandedRowKeys.includes(record.id)) {
        const itemState = itemsByMonthId[record.id];
        fetchItems(
          record.id,
          itemState?.pageNum ?? 1,
          itemState?.pageSize ?? ITEM_PAGE_SIZE,
          itemState?.filters ?? {},
        );
      }
    } finally {
      setRefreshingYearMonth(null);
    }
  };

  const openMonthItems = (record: ShopSaleIncentiveMonth) => {
    itemForm.resetFields();
    setExpandedRowKeys([record.id]);
    if (!itemsByMonthId[record.id]) {
      fetchItems(record.id, 1, ITEM_PAGE_SIZE);
    }
  };

  const handleViewItems = (record: ShopSaleIncentiveMonth) => {
    openMonthItems(record);
  };

  const handleExpand = (expanded: boolean, record: ShopSaleIncentiveMonth) => {
    if (expanded) {
      openMonthItems(record);
      return;
    }
    setExpandedRowKeys((keys) => keys.filter((key) => key !== record.id));
  };

  const handleItemSearch = (monthId: number, values: ItemFilterValues) => {
    if (!values.orderId && !values.productId) return;
    fetchItems(monthId, 1, ITEM_PAGE_SIZE, values);
  };

  const handleItemReset = (monthId: number) => {
    itemForm.resetFields();
    fetchItems(monthId, 1, ITEM_PAGE_SIZE);
  };

  const fetchAllItemsForMonth = async (monthId: number) => {
    const allItems: ShopSaleIncentiveMonthItem[] = [];
    let pageNum = 1;
    let total = 0;

    do {
      const res = await getShopSaleIncentiveItems({
        monthId,
        pageNum,
        pageSize: EXPORT_ITEM_PAGE_SIZE,
      });
      allItems.push(...res.list);
      total = res.total;
      pageNum += 1;
    } while (allItems.length < total);

    return allItems;
  };

  const buildExportRows = (items: ShopSaleIncentiveMonthItem[]) => {
    const headers = [
      t(LOCALS.shop_sale_incentive_row_type),
      t(LOCALS.commission_snapshot_order_id),
      t(LOCALS.shop_sale_incentive_order_staff),
      t(LOCALS.commission_snapshot_order_create_time),
      t(LOCALS.commission_snapshot_payment_day),
      t(LOCALS.commission_snapshot_receive_time),
      t(LOCALS.currency),
      t(LOCALS.commission_snapshot_product_id),
      t(LOCALS.product_source),
      t(LOCALS.commission_snapshot_product_quantity),
      t(LOCALS.commission_snapshot_is_tax_free),
      t(LOCALS.commission_snapshot_order_total_tax),
      t(LOCALS.commission_snapshot_product_cost_price),
      t(LOCALS.commission_snapshot_item_pay_amount),
      t(LOCALS.commission_snapshot_item_commission),
      t(LOCALS.shop_sale_incentive_profit),
      t(LOCALS.shop_sale_incentive_purchase_staff),
      t(LOCALS.shop_sale_incentive_purchase_reception_staff),
    ];

    const rows = items.map((item) => [
      rowTypeMap[item.rowType]?.label ?? item.rowType,
      item.orderId,
      item.employeeName ?? '',
      item.orderCreateTime ? formatTime(item.orderCreateTime) : '',
      item.paymentTime ? formatTime(item.paymentTime) : '',
      item.receiveTime ? formatTime(item.receiveTime) : '',
      item.currency ?? '',
      item.productId,
      item.productSource != null
        ? (productSourceMap[item.productSource]?.label ?? item.productSource)
        : '',
      item.productQuantity,
      item.isTaxFree === 1 ? t(LOCALS.yes) : t(LOCALS.no),
      toExportAmount(item.orderTotalTax),
      toExportAmount(item.productCostPrice),
      toExportAmount(item.itemAllocatedPayAmount),
      toExportAmount(item.itemAllocatedCommission),
      toExportAmount(item.profit),
      item.staffName ?? '',
      item.receptionStaffName ?? '',
    ]);

    return [headers, ...rows];
  };

  /** 按员工姓名、币种汇总销售金额、提成和利润。员工姓名是这笔订单的利润分配人。退款行金额本身为负，直接相加即净额。 */
  const buildSummaryRows = (items: ShopSaleIncentiveMonthItem[]) => {
    const totals = new Map<
      string,
      {
        employeeName: string;
        currency: string;
        sales: number;
        commission: number;
        profit: number;
      }
    >();

    for (const item of items) {
      const employeeName = item.employeeName?.trim() || '';
      const currency = item.currency?.trim() || '';
      const key = `${employeeName}\0${currency}`;
      const current = totals.get(key) ?? {
        employeeName,
        currency,
        sales: 0,
        commission: 0,
        profit: 0,
      };
      current.sales += parseExportAmount(item.itemAllocatedPayAmount);
      current.commission += parseExportAmount(item.itemAllocatedCommission);
      current.profit += parseExportAmount(item.profit);
      totals.set(key, current);
    }

    const rows: {
      employeeName: string;
      currency: string;
      sales: number;
      commission: number;
      profit: number;
    }[] = [];
    totals.forEach((row) => {
      rows.push(row);
    });
    rows.sort((a, b) => {
      const byEmployee = a.employeeName.localeCompare(b.employeeName, 'zh');
      if (byEmployee !== 0) return byEmployee;
      return a.currency.localeCompare(b.currency);
    });

    const headers = [
      t(LOCALS.shop_sale_incentive_order_staff),
      t(LOCALS.currency),
      t(LOCALS.shop_sale_incentive_summary_sales_amount),
      t(LOCALS.shop_sale_incentive_summary_commission),
      t(LOCALS.shop_sale_incentive_profit),
    ];

    return [
      headers,
      ...rows.map((row) => [
        row.employeeName || '-',
        row.currency || '-',
        roundExportAmount(row.sales),
        roundExportAmount(row.commission),
        roundExportAmount(row.profit),
      ]),
    ];
  };

  const handleDownload = async (
    record: ShopSaleIncentiveMonth,
    format: ExportFormat,
  ) => {
    if (record.status !== 'completed') return;

    setDownloadingMonthId(record.id);
    try {
      const items = await fetchAllItemsForMonth(record.id);
      if (items.length === 0) {
        message.warning(
          t(LOCALS.daily_register_close_no_recycle_order_daily_data),
        );
        return;
      }

      const data = buildExportRows(items);
      const summary = buildSummaryRows(items);
      const baseName = `shop-sale-incentive-${record.yearMonth}`;

      if (format === 'csv') {
        exportToCsv(
          [...data, [], [t(LOCALS.shop_sale_incentive_summary)], ...summary],
          `${baseName}.csv`,
        );
        return;
      }

      exportToExcel(
        [
          {
            sheetName: record.yearMonth,
            data,
          },
          {
            sheetName: t(LOCALS.shop_sale_incentive_summary).slice(0, 31),
            data: summary,
          },
        ],
        `${baseName}.xlsx`,
      );
    } finally {
      setDownloadingMonthId(null);
    }
  };

  const monthColumns: ColumnsType<ShopSaleIncentiveMonth> =
    withNowrapColumnTitles([
      {
        title: t(LOCALS.shop_sale_incentive_year_month),
        dataIndex: 'yearMonth',
        width: 110,
      },
      {
        title: t(LOCALS.shop_sale_incentive_status),
        dataIndex: 'status',
        width: 100,
        render: (status: ShopSaleIncentiveMonthStatus) => {
          const cfg = statusMap[status];
          return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : status;
        },
      },
      {
        title: t(LOCALS.shop_sale_incentive_item_count),
        dataIndex: 'itemCount',
        width: 100,
        render: (v: number | null) => (v == null ? '-' : v),
      },
      {
        title: t(LOCALS.shop_sale_incentive_calculated_at),
        dataIndex: 'calculatedAt',
        width: 160,
        render: renderTime,
      },
      {
        title: t(LOCALS.commission_snapshot_importer),
        dataIndex: 'createdBy',
        width: 120,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: t(LOCALS.shop_sale_incentive_error_message),
        dataIndex: 'errorMessage',
        ellipsis: true,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: t(LOCALS.operation),
        width: 300,
        fixed: 'right',
        render: (_, record) => (
          <Space size={4} className="flex-nowrap whitespace-nowrap">
            <Button
              type="link"
              icon={<EyeOutlined />}
              size="small"
              disabled={record.status === 'running'}
              onClick={() => handleViewItems(record)}
            >
              {t(LOCALS.view)}
            </Button>
            <Dropdown
              disabled={record.status !== 'completed'}
              menu={{
                items: [
                  {
                    key: 'xlsx',
                    label: t(LOCALS.download_excel),
                  },
                  {
                    key: 'csv',
                    label: t(LOCALS.download_csv),
                  },
                ],
                onClick: ({ key }) =>
                  handleDownload(record, key as ExportFormat),
              }}
            >
              <Button
                type="link"
                icon={<DownloadOutlined />}
                size="small"
                disabled={record.status !== 'completed'}
                loading={downloadingMonthId === record.id}
              >
                {t(LOCALS.shop_sale_incentive_download)}{' '}
                <DownOutlined className="text-xs" />
              </Button>
            </Dropdown>
            <Popconfirm
              title={t(LOCALS.shop_sale_incentive_refresh_confirm_title)}
              description={t(LOCALS.shop_sale_incentive_refresh_confirm_desc)}
              onConfirm={() => handleRefresh(record)}
              okText={t(LOCALS.confirm)}
              cancelText={t(LOCALS.cancel)}
              disabled={record.status === 'running'}
            >
              <Button
                type="link"
                icon={<ReloadOutlined />}
                size="small"
                loading={refreshingYearMonth === record.yearMonth}
                disabled={record.status === 'running'}
              >
                {t(LOCALS.shop_sale_incentive_refresh)}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ]);

  const itemColumns: ColumnsType<ShopSaleIncentiveMonthItem> =
    withNowrapColumnTitles([
      {
        title: t(LOCALS.shop_sale_incentive_row_type),
        dataIndex: 'rowType',
        width: 100,
        render: (v: string) => {
          const cfg = rowTypeMap[v];
          return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : v;
        },
      },
      {
        title: t(LOCALS.commission_snapshot_order_id),
        dataIndex: 'orderId',
        width: 110,
        render: (v: number) => (
          <LinkButton href={`/oms/order-view/${v}`}>{v}</LinkButton>
        ),
      },
      {
        title: t(LOCALS.shop_sale_incentive_order_staff),
        dataIndex: 'employeeName',
        width: 110,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: t(LOCALS.commission_snapshot_order_create_time),
        dataIndex: 'orderCreateTime',
        width: 160,
        render: renderTime,
      },
      {
        title: t(LOCALS.commission_snapshot_payment_day),
        dataIndex: 'paymentTime',
        width: 160,
        render: renderTime,
      },
      {
        title: t(LOCALS.commission_snapshot_receive_time),
        dataIndex: 'receiveTime',
        width: 160,
        render: renderTime,
      },
      {
        title: t(LOCALS.currency),
        dataIndex: 'currency',
        width: 80,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: t(LOCALS.commission_snapshot_product_id),
        dataIndex: 'productId',
        width: 100,
        render: (v: number) => (
          <LinkButton href={`/pms/product-view/${v}`}>{v}</LinkButton>
        ),
      },
      {
        title: t(LOCALS.product_source),
        dataIndex: 'productSource',
        width: 90,
        render: (v: 0 | 1 | 2 | null) => {
          if (v == null) return '-';
          const cfg = productSourceMap[v];
          return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : v;
        },
      },
      {
        title: t(LOCALS.commission_snapshot_product_quantity),
        dataIndex: 'productQuantity',
        width: 90,
      },
      {
        title: t(LOCALS.commission_snapshot_is_tax_free),
        dataIndex: 'isTaxFree',
        width: 90,
        render: (v: 0 | 1) => (v === 1 ? t(LOCALS.yes) : t(LOCALS.no)),
      },
      {
        title: t(LOCALS.commission_snapshot_order_total_tax),
        dataIndex: 'orderTotalTax',
        width: 120,
        align: 'right',
        render: moneyText,
      },
      {
        title: t(LOCALS.commission_snapshot_product_cost_price),
        dataIndex: 'productCostPrice',
        width: 110,
        align: 'right',
        render: moneyText,
      },
      {
        title: t(LOCALS.commission_snapshot_item_pay_amount),
        dataIndex: 'itemAllocatedPayAmount',
        width: 150,
        align: 'right',
        render: moneyText,
      },
      {
        title: t(LOCALS.commission_snapshot_item_commission),
        dataIndex: 'itemAllocatedCommission',
        width: 130,
        align: 'right',
        render: moneyText,
      },
      {
        title: t(LOCALS.shop_sale_incentive_profit),
        dataIndex: 'profit',
        width: 110,
        align: 'right',
        render: moneyText,
      },
      {
        title: t(LOCALS.shop_sale_incentive_purchase_staff),
        dataIndex: 'staffName',
        width: 110,
        render: (v: string | null) => v ?? '-',
      },
      {
        title: t(LOCALS.shop_sale_incentive_purchase_reception_staff),
        dataIndex: 'receptionStaffName',
        width: 130,
        render: (v: string | null) => v ?? '-',
      },
    ]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Title level={4} style={{ margin: 0 }}>
          {t(LOCALS.shop_sale_incentive)}
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCalculateOpen(true)}
        >
          {t(LOCALS.shop_sale_incentive_calculate)}
        </Button>
      </div>

      <Form
        form={listForm}
        layout="inline"
        onFinish={handleListSearch}
        className="mb-4"
      >
        <Form.Item
          name="yearMonth"
          label={t(LOCALS.shop_sale_incentive_filter_year_month)}
          className="mb-0"
        >
          <DatePicker picker="month" allowClear style={{ width: 150 }} />
        </Form.Item>
        <Form.Item
          name="status"
          label={t(LOCALS.shop_sale_incentive_filter_status)}
          className="mb-0"
        >
          <Select
            allowClear
            style={{ width: 140 }}
            options={(
              Object.keys(statusMap) as ShopSaleIncentiveMonthStatus[]
            ).map((value) => ({
              value,
              label: statusMap[value].label,
            }))}
          />
        </Form.Item>
        <Form.Item className="mb-0">
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              {t(LOCALS.search)}
            </Button>
            <Button onClick={handleListReset}>{t(LOCALS.reset)}</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        loading={listLoading}
        columns={monthColumns}
        dataSource={list}
        scroll={{ x: 1100 }}
        rowClassName={(record) =>
          expandedRowKeys.includes(record.id) ? 'ant-table-row-selected' : ''
        }
        expandable={{
          expandedRowKeys,
          showExpandColumn: false,
          onExpand: handleExpand,
          expandedRowRender: (record) => {
            const itemState = itemsByMonthId[record.id] ?? emptyItemPage();
            return (
              <div className="py-4">
                <Form
                  form={itemForm}
                  layout="inline"
                  onFinish={(values) => handleItemSearch(record.id, values)}
                  className="mb-3"
                >
                  <Form.Item
                    name="orderId"
                    label={t(LOCALS.commission_snapshot_order_id)}
                    className="mb-0"
                  >
                    <InputNumber
                      placeholder={
                        t(LOCALS.commission_snapshot_input_order_id) || ''
                      }
                      controls={false}
                      style={{ width: 150 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="productId"
                    label={t(LOCALS.commission_snapshot_product_id)}
                    className="mb-0"
                  >
                    <InputNumber
                      placeholder={
                        t(LOCALS.commission_snapshot_input_product_id) || ''
                      }
                      controls={false}
                      style={{ width: 150 }}
                    />
                  </Form.Item>
                  <Form.Item className="mb-0">
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<SearchOutlined />}
                      >
                        {t(LOCALS.search)}
                      </Button>
                      <Button onClick={() => handleItemReset(record.id)}>
                        {t(LOCALS.reset)}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
                <Table
                  rowKey="id"
                  columns={itemColumns}
                  dataSource={itemState.list}
                  loading={itemState.loading}
                  scroll={{ x: 2100 }}
                  size="small"
                  pagination={{
                    current: itemState.pageNum,
                    pageSize: itemState.pageSize,
                    total: itemState.total,
                    showSizeChanger: true,
                    showTotal: (n) =>
                      t(LOCALS.commission_snapshot_total_count, { total: n }),
                    onChange: (page, newPageSize) => {
                      fetchItems(
                        record.id,
                        page,
                        newPageSize,
                        itemState.filters,
                      );
                    },
                  }}
                />
              </div>
            );
          },
        }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (n) =>
            t(LOCALS.commission_snapshot_total_count, { total: n }),
          onChange: (page, newPageSize) => {
            setPageNum(page);
            if (newPageSize !== pageSizeRef.current) {
              pageSizeRef.current = newPageSize;
              setPageSize(newPageSize);
            }
            setExpandedRowKeys([]);
            fetchList(page, getListFilters());
          },
        }}
      />

      <Modal
        title={t(LOCALS.shop_sale_incentive_calculate_modal_title)}
        open={calculateOpen}
        onOk={handleCalculate}
        onCancel={() => {
          setCalculateOpen(false);
          calculateForm.resetFields();
        }}
        okText={t(LOCALS.shop_sale_incentive_calculate)}
        cancelText={t(LOCALS.cancel)}
        confirmLoading={calculateLoading}
        destroyOnClose
      >
        <Form form={calculateForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="yearMonth"
            label={t(LOCALS.shop_sale_incentive_year_month)}
            rules={[
              {
                required: true,
                message:
                  t(LOCALS.shop_sale_incentive_please_select_month) || '',
              },
            ]}
          >
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              disabledDate={(current) =>
                current != null && current > dayjs().endOf('month')
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
